/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  partnerApi, fmtPartnerErr, clearPartnerToken, isPartnerAuthed,
} from "../../lib/partnerApi";
import {
  ArrowLeft, SignOut, ShoppingBag, Package, CheckCircle, XCircle,
  Clock, Truck, ArrowRight, Phone, MapPin, User, Receipt,
} from "@phosphor-icons/react";

const STATUS_FLOW = ["placed", "confirmed", "in_progress", "delivered"];
const STATUS_META = {
  placed:      { label: "Placed",       Icon: Clock,       color: "#0B2B66" },
  confirmed:   { label: "Confirmed",    Icon: CheckCircle, color: "#2D5A27" },
  in_progress: { label: "In progress",  Icon: Truck,       color: "#C9A227" },
  delivered:   { label: "Delivered",    Icon: Package,     color: "#1A3317" },
  cancelled:   { label: "Cancelled",    Icon: XCircle,     color: "#7A2E18" },
};

export default function PartnerOrders() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const params = filter ? `?status=${filter}` : "";
      const r = await partnerApi.get(`/service-partners/me/orders${params}`);
      setOrders(r.data.orders || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      if (e?.response?.status === 401) {
        clearPartnerToken();
        nav("/service-partner/login", { replace: true });
      } else toast.error(fmtPartnerErr(e));
    }
    setLoading(false);
  };
  useEffect(() => {
    if (!isPartnerAuthed()) { nav("/service-partner/login", { replace: true }); return; }
    load();
  }, [filter]);

  const advanceStatus = async (o, next) => {
    try {
      await partnerApi.put(`/service-partners/me/orders/${o.id}/status`,
                            { status: next });
      toast.success(`Order ${o.order_no} → ${STATUS_META[next].label}`);
      // Refresh detail panel + list
      const fresh = await partnerApi.get(`/service-partners/me/orders/${o.id}`);
      setSelected(fresh.data);
      load();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };

  if (loading) return <Loading/>;

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="partner-orders">
      <header className="bg-white border-b border-[#E5E2D9] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/service-partner/dashboard" className="inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:text-[#2D5A27]">
            <ArrowLeft size={11}/> Dashboard
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/service-partner/listings" className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <Package size={12}/> Listings
            </Link>
            <Link to="/service-partner/deliveries" className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <Truck size={12}/> Deliveries
            </Link>
            <button onClick={() => { clearPartnerToken(); nav("/service-partner/login"); }}
                     className="h-9 px-3 inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:bg-[#F7F6F2] rounded">
              <SignOut size={12}/> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">Storefront orders</div>
          <h1 className="font-heading text-3xl mt-1 text-[#1A1A1A]">Orders</h1>
        </div>

        {/* Status filter chips */}
        <div className="flex gap-1.5 flex-wrap" data-testid="order-filters">
          <Chip on={!filter} onClick={() => setFilter("")}
                 label={`All · ${Object.values(counts).reduce((a, b) => a + b, 0)}`} testid="filter-all"/>
          {STATUS_FLOW.concat(["cancelled"]).map(s => (
            <Chip key={s} on={filter === s} onClick={() => setFilter(s)}
                   label={`${STATUS_META[s].label} · ${counts[s] || 0}`}
                   testid={`filter-${s}`}/>
          ))}
        </div>

        {orders.length === 0 ? (
          <div className="bg-white border border-[#E5E2D9] rounded-xl p-10 text-center text-[#8A8780]">
            No orders yet.
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Orders list */}
            <div className="space-y-2">
              {orders.map(o => {
                const m = STATUS_META[o.status] || STATUS_META.placed;
                return (
                  <button key={o.id} onClick={() => setSelected(o)}
                          data-testid={`order-row-${o.id}`}
                          className={`w-full text-left bg-white border rounded-md p-4 flex items-center gap-3 hover:border-[#2D5A27] transition ${
                            selected?.id === o.id ? "border-[#2D5A27] shadow-sm" : "border-[#E5E2D9]"
                          }`}>
                    <div className="w-10 h-10 rounded-md grid place-items-center flex-none"
                          style={{ background: m.color + "20", color: m.color }}>
                      <m.Icon size={14} weight="duotone"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] text-[#8A8780]">{o.order_no}</div>
                      <div className="font-heading text-base text-[#1A1A1A] truncate">{o.customer_name}</div>
                      <div className="text-[11px] text-[#5C5A55]">
                        {o.items.length} item{o.items.length > 1 ? "s" : ""} · ₹{o.total.toFixed(2)}
                        {o.has_rental && <span className="ml-2 px-1 py-0.5 bg-[#F4C7B9] text-[#7A2E18] text-[9px] uppercase rounded">Rental</span>}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded`}
                          style={{ background: m.color + "20", color: m.color }}>
                      {m.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Detail panel */}
            {selected && (
              <aside className="bg-white border border-[#E5E2D9] rounded-xl p-5 space-y-4 lg:sticky lg:top-20 lg:self-start"
                      data-testid="order-detail">
                <div>
                  <div className="font-mono text-[10px] text-[#8A8780]">{selected.order_no}</div>
                  <div className="font-heading text-xl">{selected.customer_name}</div>
                </div>

                {/* Status flow */}
                <StatusFlow o={selected} onAdvance={(n) => advanceStatus(selected, n)}/>

                <div className="space-y-1.5 text-xs">
                  <DetRow Icon={User} k="Customer" v={selected.customer_name}/>
                  <DetRow Icon={Phone} k="Phone" v={selected.delivery_phone || selected.customer_phone}/>
                  <DetRow Icon={MapPin} k="Deliver to" v={selected.delivery_address}/>
                  {selected.customer_notes && <DetRow Icon={Receipt} k="Notes" v={selected.customer_notes}/>}
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5A55] mb-2">Items</div>
                  <div className="space-y-1.5">
                    {selected.items.map((it, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 text-xs">
                        <div className="flex-1 min-w-0">
                          <div className="text-[#1A1A1A] font-medium">{it.name}</div>
                          <div className="text-[10px] text-[#8A8780] capitalize">
                            {it.qty} × {it.type === "rental" ? `${it.rental_days || 1} day${(it.rental_days||1)>1?"s":""}` : it.unit}
                          </div>
                        </div>
                        <div className="font-mono text-[#1A1A1A]">₹{it.line_total.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-1 text-xs border-t border-[#F0ECE3] pt-3">
                  <div className="flex justify-between"><span className="text-[#5C5A55]">Subtotal</span><span className="font-mono">₹{selected.subtotal.toFixed(2)}</span></div>
                  {selected.deposit_total > 0 && (
                    <div className="flex justify-between"><span className="text-[#5C5A55]">Refundable deposit</span><span className="font-mono">₹{selected.deposit_total.toFixed(2)}</span></div>
                  )}
                  <div className="flex justify-between font-medium pt-1 border-t border-[#F0ECE3]">
                    <span>Total payable</span>
                    <span className="font-mono text-base">₹{selected.total.toFixed(2)}</span>
                  </div>
                  <div className="text-[10px] text-[#8A8780] uppercase tracking-[0.18em] pt-1">
                    Payment · {selected.payment_method} · {selected.payment_status}
                  </div>
                </div>
              </aside>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function StatusFlow({ o, onAdvance }) {
  const curIdx = STATUS_FLOW.indexOf(o.status);
  const isCancelled = o.status === "cancelled";
  return (
    <div className="bg-[#F7F6F2] rounded-md p-3 space-y-2.5">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
        Status flow
      </div>
      <div className="flex items-center gap-1.5">
        {STATUS_FLOW.map((s, i) => {
          const m = STATUS_META[s];
          const done = !isCancelled && i <= curIdx;
          return (
            <React.Fragment key={s}>
              <div className={`flex-1 flex flex-col items-center gap-1 ${done ? "opacity-100" : "opacity-40"}`}>
                <div className="w-7 h-7 rounded-full grid place-items-center" style={{ background: done ? m.color : "#E5E2D9", color: done ? "white" : "#8A8780" }}>
                  <m.Icon size={11} weight="fill"/>
                </div>
                <div className="text-[9px] uppercase tracking-[0.15em] text-center" style={{ color: done ? m.color : "#8A8780" }}>{m.label}</div>
              </div>
              {i < STATUS_FLOW.length - 1 && <div className={`flex-1 h-0.5 ${i < curIdx && !isCancelled ? "bg-[#2D5A27]" : "bg-[#E5E2D9]"}`}/>}
            </React.Fragment>
          );
        })}
      </div>
      {!isCancelled && curIdx < STATUS_FLOW.length - 1 && (
        <div className="flex gap-2 pt-2">
          <button onClick={() => onAdvance(STATUS_FLOW[curIdx + 1])}
                   data-testid="btn-advance"
                   className="flex-1 h-9 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-xs font-medium inline-flex items-center justify-center gap-1.5">
            Mark as {STATUS_META[STATUS_FLOW[curIdx + 1]].label} <ArrowRight size={10} weight="bold"/>
          </button>
          {o.status === "placed" && (
            <button onClick={() => onAdvance("cancelled")}
                     className="h-9 px-3 rounded border border-[#F4C7B9] text-[#7A2E18] text-xs hover:bg-[#FBE7E0]">
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const Chip = ({ on, onClick, label, testid }) => (
  <button onClick={onClick} data-testid={testid}
           className={`px-3 py-1.5 text-xs rounded-full border transition ${
             on ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:border-[#2D5A27]"
           }`}>{label}</button>
);
const DetRow = ({ Icon, k, v }) => (
  <div className="flex items-start gap-2">
    <Icon size={11} className="text-[#8A8780] mt-1 flex-none"/>
    <div className="flex-1 min-w-0">
      <span className="text-[#8A8780] uppercase tracking-[0.15em] text-[10px] mr-2">{k}</span>
      <span className="text-[#1A1A1A]">{v}</span>
    </div>
  </div>
);
const Loading = () => (
  <div className="min-h-screen grid place-items-center bg-[#F7F6F2] text-sm text-[#5C5A55]">Loading orders…</div>
);
